<script setup>
/**
 * BrandLogo — Logotipo y wordmark de la marca (AppMisionate).
 *
 * Fuente única de la marca: el ícono es el asset canónico
 * `/images/appmisionate-icon.svg` (birrete + monitor + check, azul #0D47A1
 * y rojo #E53935) y el wordmark es texto HTML real ("App" + "misiónate"),
 * no un `<text>` dentro del SVG: el texto embebido depende de la fuente
 * disponible y se recorta cuando Poppins no está cargada.
 *
 * `variant`:
 *   - 'light' → wordmark en blanco. Para fondos oscuros (sidebar, footer, aula).
 *   - 'dark'  → wordmark en azul de marca. Para fondos claros (páginas públicas).
 *
 * `text-class` permite enganchar el texto a las reglas de colapso
 * que ya existen en cada sidebar (`.sidebar-logo-text`, `.sidebar-brand-text`).
 *
 * `stacked` apila el nombre debajo del ícono (en vez de al lado) y centra
 * el bloque. Se usa en el panel de branding del login.
 *
 * `size` escala ícono + wordmark: 'sm' | 'md' | 'lg'.
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'light',
    validator: (value) => ['light', 'dark'].includes(value),
  },
  textClass: {
    type: String,
    default: '',
  },
  stacked: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
})
</script>

<template>
  <span
    class="brand-logo"
    :class="[
      `brand-logo--${props.variant}`,
      `brand-logo--${props.size}`,
      { 'brand-logo--stacked': props.stacked },
    ]"
  >
    <span class="brand-logo-icon" aria-hidden="true">
      <img src="/images/appmisionate-icon.svg" alt="">
    </span>
    <span class="brand-logo-text" :class="textClass">
      <span class="brand-logo-app">App</span>misiónate
    </span>
  </span>
</template>

<style scoped>
.brand-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

/* ── Escalas de tamaño (ícono + wordmark) ── */
.brand-logo--sm {
  --brand-logo-icon-size: 30px;
  --brand-logo-glyph-size: 22px;
  --brand-logo-text-size: 0.9rem;
}

.brand-logo--md {
  --brand-logo-icon-size: 34px;
  --brand-logo-glyph-size: 26px;
  --brand-logo-text-size: 1rem;
}

.brand-logo--lg {
  --brand-logo-icon-size: 96px;
  --brand-logo-glyph-size: 74px;
  --brand-logo-text-size: 2rem;
}

.brand-logo--stacked {
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  text-align: center;
}

.brand-logo-icon {
  width: var(--brand-logo-icon-size, 34px);
  height: var(--brand-logo-icon-size, 34px);
  background: #fff;
  border-radius: calc(var(--brand-logo-icon-size, 34px) * 0.26);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .28);
}

.brand-logo-icon img {
  width: var(--brand-logo-glyph-size, 26px);
  height: var(--brand-logo-glyph-size, 26px);
  display: block;
}

/* Doble clase para ganar especificidad a las reglas globales del sidebar
   (.sidebar-ap .sidebar-logo-text) sin recurrir a !important. */
.brand-logo .brand-logo-text {
  font-family: 'Poppins', sans-serif;
  font-size: var(--brand-logo-text-size, 1rem);
  font-weight: 800;
  letter-spacing: -.02em;
  white-space: nowrap;
  overflow: hidden;
}

.brand-logo--light .brand-logo-text {
  color: #f8fafc;
}

.brand-logo--dark .brand-logo-text {
  color: #0D47A1;
}

.brand-logo .brand-logo-app {
  color: #64b5f6;
}
</style>
